import { ConfigProvider } from "antd";
import "antd/dist/reset.css";
import React, { useEffect, useRef } from "react";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ProjectName } from "./config";
import { persistor, store } from "./redux/store";
import Routers from "./routers";
import GlobalStyles from "./styles/GlobalStyles";

/**
 * PENDO INITIALIZATION WRAPPER
 * This component listens to the Redux store. When a user logs in,
 * it passes their identity to Pendo.
 */
function PendoWrapper({ children }) {
  // Grab user data from Redux state
  const userData = useSelector(
    (state) =>
      state.user?.data?.user ||
      state.user?.user ||
      state.session?.data?.user
  );
  const pendoInitializedRef = useRef(false);

  useEffect(() => {
    if (!userData) return;

    const visitorId = userData.user_data?.id || userData.id;
    if (!visitorId) return;

    const payload = {
      visitor: {
        id: visitorId, // REQUIRED: Must be 'id'
        email: userData.user_data?.email,
        role: userData.user_data?.user_type,
        full_name: userData.user_data?.full_name,
      },
      account: {
        // Optional: If you have company data, map it here
        id: userData.user_data?.company_id || "INDIVIDUAL",
        name: userData.user_data?.company_name || "Personal Account",
      },
    };

    const runPendo = () => {
      if (!window.pendo || typeof window.pendo.initialize !== "function") return false;

      if (!pendoInitializedRef.current) {
        window.pendo.initialize(payload);
        pendoInitializedRef.current = true;
      } else if (typeof window.pendo.identify === "function") {
        window.pendo.identify(payload.visitor, payload.account);
      }

      return true;
    };

    // Try immediately first.
    if (runPendo()) return;

    // If Pendo script is still loading, retry briefly until it's available.
    const intervalId = window.setInterval(() => {
      if (runPendo()) {
        window.clearInterval(intervalId);
      }
    }, 300);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [userData]); // Re-run whenever the user logs in or out

  return children;
}

function App() {
  document.title = ProjectName;

  const handleLoad = () => {
    console.log("Page fully loaded including stylesheets, images, etc.");
  };

  useEffect(() => {
    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* We wrap the app logic with PendoWrapper so it has access to Redux state */}
          <PendoWrapper>
            <ConfigProvider
                theme={{
                  token: {
                    fontFamily: `"Inter", sans-serif`,
                  },
                  components: {
                    Checkbox: {
                      colorPrimary: "#F45B68",
                      colorPrimaryHover: "#cd404c",
                      colorPrimaryActive: "#F45B68",
                      borderRadius: 4,
                      controlHeight: 20,
                      size: 16,
                      borderColor: "#ddd",
                      colorBgContainer: "#fff",
                      colorText: "#333",
                      colorTextDisabled: "#bdbdbd",
                      colorBorder: "#ddd",
                    },
                    Switch: {
                      colorPrimary: "#F45B68",
                      colorPrimaryHover: "#cd404c",
                      colorPrimaryActive: "#F45B68",
                      handleBg: "#fff",
                      trackHeight: 26,
                      trackMinWidth: 38,
                      handleSize: 18,
                      borderRadius: 22,
                      colorText: "#333",
                      colorTextTertiary: "#bdbdbd",
                      colorBorder: "#ddd",
                    },
                      Input: {
                          colorPrimary: '#F45B68',
                          colorPrimaryHover: '#cd404c',
                          colorPrimaryActive: '#F45B68',
                          borderRadius: 8,
                          colorBgContainer: "#fff",
                          colorText: "#333",
                          // colorTextPlaceholder: "#bdbdbd",
                          colorBorder: "#ddd",
                          controlHeight: 40,
                          fontSize: 13,
                      },
                  },
                }}
            >
              <GlobalStyles />
              <Routers />
            </ConfigProvider>
          </PendoWrapper>
        </PersistGate>
      </Provider>
  );
}

export default App;